import React, { ReactElement } from 'react';
import { ActionType, IAlert, IMetaAction, Maybe } from '../../graphql/schema/p4types';
import { EntityType } from '../../@types/entityTypes';
import { IActionType } from '../connectedForm/Hooks/usePerformSave';
import { SubmitOption } from '../../@types/connectedFormTypes';
import { temporarySaveSucceeded } from '../connectedForm/utils/actions';

const ConnectedFormContext = React.createContext({} as ConnectedFormContextType);
export default ConnectedFormContext;

export type IConnectedFormModalProps = {
  modalOpen: boolean;
  metaAction: IMetaAction;
};

export type IConnectedFormModals = {
  [key in ActionType]?: {
    order: number;
    render?: (props: any) => ReactElement;
    component?: (props: IConnectedFormModalProps) => ReactElement; //Technically different to render prop above - Render
    // gets treated like a function, component
    // properly as a function
    // component.
    condition?: (props: any) => boolean;
  };
};

//Helper to unwrap the parameters - Need to drop the 'first' parameter because it's bound in ConnectedForm (I.e. is always 'entity.type' via function bind).
type ITemporarySaveSucceededParams = Parameters<typeof temporarySaveSucceeded>;

//Distinguish the functions available from ConnectedFormContext which are forwarded to redux.
type IConnectedFormReduxFunctions = {
  temporarySaveSucceeded: (
    temporarySaveResult: ITemporarySaveSucceededParams[0],
    submittedValues: ITemporarySaveSucceededParams[1],
    mutationId: ITemporarySaveSucceededParams[2],
    ignoreMutationIdCheck: ITemporarySaveSucceededParams[3]
  ) => { type: string; meta: any; payload: { values: any; valuesSubmitted: any } };
  setSubmitFailed: Function;
  stopSubmit: Function;
  formTemporaryUpdate: Function;
};

//TODO - Rename to 'IConnectedFormContext' - Consistency.
export type ConnectedFormContextType<SubmitResult = any> = IConnectedFormReduxFunctions & {
  name: string;
  cancel?: () => void;
  submit?: () => void;
  entity: EntityType; //Not optional - Provide an entity (If usage is a 'non-form' that uses this context, cast as appropriate).
  readOnly?: boolean;
  getFormState?: (store: object) => { values: object };
  register: (dataid: string, type?: string, tabId?: string) => void;
  unregister: (dataid: string) => void;
  getValues?: () => any;
  fieldValidation?: Array<any>;
  values?: {
    id: number;
    uuid: string;
    concurrencyVersion: number;
    [key: string]: any; //Allow any keys for values, 100's of different possibilities.
  };
  baseValues?: any; //The state of values the last time concurrency/id changed (As used by the pristine hook to check for outstanding changes).
  validationErrors?: any;
  registeredFields?: { [dataid: string]: any };
  blur: (
    field: string,
    value: any,
    mutationId?: string
  ) => {
    type: string;
    meta: { form: string; field: string; touch: boolean; mutationId?: string };
    payload: any;
  };
  change: (
    field: string | undefined | false, // if false replaces form values with payload
    value: any,
    mutationId?: string
  ) => {
    type: string;
    meta: { form: string; field: string; touch: boolean; mutationId?: string };
    payload: any;
  };
  initialized?: any;
  reloadData?: (processData?: boolean, options?: { getDefault: boolean; isConcurrencyReload: boolean }) => Promise<any>; //Reloads the data on the current form - If id/uuid are unchanged, must set processData flag to true or values won't be updated as expected.
  concurrencyReloadData?: (ignoreSubmitOption?: boolean) => void; //Check if concurrency has changed - If it has, reload and smart-merge the data.
  submitFunctions?: {
    submitInitiated: any;
    submitCompleted: ({ result, action }: { result: SubmitResult; action: IActionType<SubmitResult> }) => SubmitResult;
    submitFailed: any;
  };
  submitInProgress?: boolean;
  setSubmitInProgress?: (inProgress: boolean) => void;
  submitOptions?: [
    selectedSubmitOption: { value: SubmitOption },
    dispatchSubmitOption: (action: { type: string }) => void
  ];
  screenReference?: string;
  afterSubmit?: (options?: { result: any }) => void;
  pristine?: boolean;
  dirty?: boolean;
  headerAlerts?: Array<IAlert>;
  removeHeaderAlert?: (alert: IAlert) => void;
  match?: {
    params?: any;
  };
  supportingData?: {
    [key: string]: any;
  };
  setSupportingData?: (newSupportingData: any | ((existingSupportingData: any) => any)) => void;
  addHeaderAlert?: (alert: IAlert) => void;
  disableModalClose?: boolean;
  formData?: any;
  newform: {
    [key: string]: Maybe<{
      initial: Maybe<{ [key: string]: any }>;
      meta: Array<any>;
      registeredFields: { [key: string]: any };
      values: Maybe<{ [key: string]: any }>;
    }>;
  };
  form?: string;
  modal?: IConnectedFormModals;
  location?: any;
  getSubmitValues?: (parameters: { values: any }, temporary: boolean) => any;
  suppressTemporarySaveUpdate?: boolean; //Only one use - ProductGoodsReciptForm
};
